import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import tw from 'twin.macro';
import FsLightbox from 'fslightbox-react';
import { GetStaticProps, GetStaticPropsResult } from 'next';

import Layout from '@app/layouts/page-default';
import { Cinema, CinemaSingle } from '@app/types/cinema';
import { Movie, MovieSingle, MovieStatus } from '@app/types/movie';
import MovieCard from '@app/components/movie-card';
import { firebase } from '@app/config/fire-conf';
import SEO from '@app/components/seo';
import CinemaLobby from '@app/components/cinema-lobby';

type CinemasProps = {
  moviesStrObjs: string[];
  cinemasStrObjs: string[];
};

const CinemaBlock = dynamic(() => import('../components/cinema-detail'), {
  ssr: false,
});

export const MovieDetailContentBlockStyled = styled.div`
  ${tw`flex flex-col mx-auto z-10 w-full justify-between py-32 pb-8 relative`}
  max-width: 1360px;

  .cinema-lobby,
  .C2,
  .C3 {
    ${tw`relative lg:pt-20`}

    &:before {
      content: '';
      right: 20%;
      width: 1000%;
      max-width: 2174px;
      ${tw`lg:border border-gray-300 top-0 absolute`}
    }
  }

  .C3 {
    &:before {
      left: 20%;
      right: auto;
    }
  }

  .content-container {
    ${tw`flex w-full flex-col justify-between overflow-hidden mb-12 lg:mb-20`}

    h2 {
      ${tw`text-white text-3xl font-bold mb-6 my-6 mx-8`}
      font-size: 32px;
    }

    .movie-grid {
      ${tw`grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6 lg:gap-y-12 mx-8`}
    }
  }
`;

export default function Cinemas({ moviesStrObjs, cinemasStrObjs }: CinemasProps): JSX.Element {
  const movies: Movie[] = moviesStrObjs.map((movieStrObj) => {
    const movieSingle = JSON.parse(movieStrObj) as MovieSingle;
    return {
      ...movieSingle,
      releaseDate: firebase.firestore.Timestamp.fromMillis(movieSingle.releaseDate.seconds * 1000),
      lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
        movieSingle.lastUpdatedAt.seconds * 1000,
      ),
      premiereDate: movieSingle.premiereDate
        ? firebase.firestore.Timestamp.fromMillis(movieSingle.premiereDate.seconds * 1000)
        : undefined,
    };
  });
  const cinemas: Cinema[] = cinemasStrObjs.map((cinemaStrObj) => {
    const cinemaSingle = JSON.parse(cinemaStrObj) as CinemaSingle;
    return {
      ...cinemaSingle,
      lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
        cinemaSingle.lastUpdatedAt.seconds * 1000,
      ),
    };
  });
  const [toggler, setToggler] = useState(false);
  const [trailer, setTrailer] = useState('');
  const onWatchTrailer = (trailerUrl: string) => {
    setTrailer(trailerUrl);
    setToggler(!toggler);
  };

  const movieList = (data: Movie[], cinema?: string) => {
    const list = data.map((movie) => {
      return (
        <MovieCard
          key={movie.slug}
          poster={movie.poster.url}
          movieName={movie.name}
          trailer={movie.trailer}
          certification={movie.certification}
          releaseDate={movie.releaseDate}
          premiereDate={movie.premiereDate}
          runTime={movie.runTime}
          status={movie.status}
          slug={movie.slug}
          cinema={cinema}
          onWatchTrailer={onWatchTrailer}
        />
      );
    });

    return list;
  };

  return (
    <Layout>
      <SEO title="Cinemas" />
      <MovieDetailContentBlockStyled>
        {cinemas.map((cinema, cinemaIndex = 1) => {
          const moviesInCinema: Movie[] = [];
          movies.forEach((movie) => {
            if (movie?.availableOn && movie?.availableOn[cinema.id]) {
              moviesInCinema.push(movie);
            }
          });
          return (
            <React.Fragment key={cinema.label}>
              <CinemaBlock
                cinemaName={cinema.label}
                cinema={cinema}
                reverse={!!(cinemaIndex % 2)}
              />

              <div className="content-container">
                <h2 className="block-title">Now showing at KCC Multiplex - {cinema.label}</h2>
                <div className="movie-grid">{movieList(moviesInCinema, cinema.id)}</div>
              </div>
            </React.Fragment>
          );
        })}
        <CinemaLobby />
        <FsLightbox toggler={toggler} sources={[trailer]} />
      </MovieDetailContentBlockStyled>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps<CinemasProps> = async () => {
  const moviesQuerySnap = await firebase
    .firestore()
    .collection('movies')
    .where('status', '!=', MovieStatus.archive)
    .get();

  const moviesStrObjs: string[] = [];
  moviesQuerySnap.forEach((movie) => {
    const movieObj = { ...movie.data(), id: movie.id };
    const movieStrObj = JSON.stringify(movieObj);
    moviesStrObjs.push(movieStrObj);
  });

  const cinemasQuerySnap = await firebase.firestore().collection('cinemas').get();

  const cinemasStrObjs: string[] = [];
  cinemasQuerySnap.forEach((cinema) => {
    const cinemaObj = { ...cinema.data(), id: cinema.id };
    const cinemaStrObj = JSON.stringify(cinemaObj);
    cinemasStrObjs.push(cinemaStrObj);
  });

  const returnCinemas: GetStaticPropsResult<CinemasProps> = {
    props: {
      moviesStrObjs,
      cinemasStrObjs,
    },
    revalidate: 60,
  };

  return returnCinemas;
};
